import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Dropdown, Nav } from 'react-bootstrap';
import { isIterableArray } from 'helpers/utils';
import paths from 'routes/paths';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useCompleteNotification,
  useReopenNotification
} from 'hooks/useNotifications';
import usePinnedMobileMenu from 'hooks/usePinnedMobileMenu';
import AlertCard from 'components/alerts/AlertCard';
import CompleteAlertModal from './CompleteAlertModal';

// The dropdown shows the latest few alerts; the full list with filters and
// paging lives on the Alerts page. Both read the same cached query.
const DROPDOWN_LIMIT = 10;
const FULL_LIMIT = 500;

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [alertToComplete, setAlertToComplete] = useState(null);

  const { notifications } = useNotifications(FULL_LIMIT);
  // Only fetched while the completed view is open.
  const { notifications: completedNotifications } = useNotifications(
    FULL_LIMIT,
    showCompleted
  );
  const { markRead } = useMarkNotificationRead();
  const { markAllRead } = useMarkAllNotificationsRead();
  const { completeAlert, isCompleting } = useCompleteNotification();
  const { reopenAlert } = useReopenNotification();

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const readCount = notifications.length - unreadCount;
  const newNotifications = notifications
    .filter(n => !n.is_read)
    .slice(0, DROPDOWN_LIMIT);
  const earlierNotifications = notifications
    .filter(n => n.is_read)
    .slice(0, Math.max(0, DROPDOWN_LIMIT - newNotifications.length));
  const hiddenCount =
    notifications.length -
    newNotifications.length -
    earlierNotifications.length;

  const pinnedMenu = usePinnedMobileMenu();

  const handleToggle = () => {
    pinnedMenu.onToggle(!isOpen);
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    window.addEventListener('scroll', () => {
      window.innerWidth < 1200 && setIsOpen(false);
    });
  }, []);

  const markAsRead = e => {
    e.preventDefault();
    markAllRead();
  };

  const handleClick = notification => {
    if (!notification.is_read) {
      markRead(notification.id);
    }
    setIsOpen(false);
    if (notification.link_path) {
      navigate(notification.link_path);
    }
  };

  // Completing needs the notes modal, so the dropdown closes and the modal
  // takes over rather than the two overlapping.
  const handleCompleteClick = notification => {
    setIsOpen(false);
    setAlertToComplete(notification);
  };

  const handleConfirmComplete = notes => {
    completeAlert(
      { notificationId: alertToComplete.id, notes },
      { onSuccess: () => setAlertToComplete(null) }
    );
  };

  const goToAllAlerts = e => {
    e.preventDefault();
    setIsOpen(false);
    navigate(paths.alerts);
  };

  return (
    <>
      <Nav.Item as="li" className="me-2">
        <Dropdown align="end" show={isOpen} onToggle={handleToggle}>
          <Dropdown.Toggle
            as={Button}
            variant="falcon-default"
            size="sm"
            className="position-relative"
            ref={pinnedMenu.toggleRef}
            title="Alerts"
          >
            <FontAwesomeIcon icon="bell" className="me-md-1" />
            {/* Icon only on phones so the top bar fits */}
            <span className="d-none d-md-inline">Alerts</span>
            {unreadCount > 0 && (
              <Badge
                pill
                bg="danger"
                className="position-absolute top-0 start-100 translate-middle"
                style={{ fontSize: '0.6rem' }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="py-0"
            style={{
              minWidth: 'min(420px, 92vw)',
              maxHeight: '70vh',
              overflowY: 'auto',
              ...pinnedMenu.menuStyle
            }}
          >
            <div className="bg-light px-3 py-2 d-flex justify-content-between align-items-center border-bottom">
              <div>
                <span className="fw-bold fs-0">
                  {showCompleted ? 'Completed alerts' : 'Alerts'}
                </span>
                {!showCompleted && (
                  <div className="fs--2 text-600">
                    <span className="fw-semi-bold text-primary">
                      {unreadCount} unread
                    </span>
                    {' · '}
                    {readCount} read
                  </div>
                )}
              </div>
              <div className="d-flex gap-3">
                {!showCompleted && unreadCount > 0 && (
                  <Link
                    className="fw-normal fs--1 text-decoration-none"
                    to="#!"
                    onClick={markAsRead}
                  >
                    Mark all as read
                  </Link>
                )}
                <Link
                  className="fw-normal fs--1 text-decoration-none"
                  to="#!"
                  onClick={e => {
                    e.preventDefault();
                    setShowCompleted(!showCompleted);
                  }}
                >
                  {showCompleted ? 'Back to open' : 'Completed'}
                </Link>
              </div>
            </div>

            {showCompleted ? (
              <>
                {!isIterableArray(completedNotifications) && (
                  <div className="text-center py-4 text-muted fs-0">
                    No completed alerts yet
                  </div>
                )}
                {completedNotifications.slice(0, DROPDOWN_LIMIT).map(n => (
                  <AlertCard
                    key={n.id}
                    notification={n}
                    completed
                    onOpen={handleClick}
                    onReopen={a => reopenAlert({ notificationId: a.id })}
                  />
                ))}
              </>
            ) : (
              <>
                {!isIterableArray(notifications) && (
                  <div className="text-center py-4 text-muted fs-0">
                    No open alerts
                  </div>
                )}
                {isIterableArray(newNotifications) && (
                  <>
                    <div className="bg-light px-3 py-1 fs--2 fw-semi-bold text-500 border-bottom">
                      NEW
                    </div>
                    {newNotifications.map(n => (
                      <AlertCard
                        key={n.id}
                        notification={n}
                        onOpen={handleClick}
                        onComplete={handleCompleteClick}
                      />
                    ))}
                  </>
                )}
                {isIterableArray(earlierNotifications) && (
                  <>
                    <div className="bg-light px-3 py-1 fs--2 fw-semi-bold text-500 border-bottom">
                      EARLIER
                    </div>
                    {earlierNotifications.map(n => (
                      <AlertCard
                        key={n.id}
                        notification={n}
                        onOpen={handleClick}
                        onComplete={handleCompleteClick}
                      />
                    ))}
                  </>
                )}
              </>
            )}

            <div className="bg-light px-3 py-2 text-center border-top position-sticky bottom-0">
              <Link
                to={paths.alerts}
                className="fw-semi-bold fs--1 text-decoration-none"
                onClick={goToAllAlerts}
              >
                View all alerts
                {!showCompleted && hiddenCount > 0 && ` (${hiddenCount} more)`}
                <FontAwesomeIcon icon="chevron-right" className="ms-1" />
              </Link>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </Nav.Item>

      <CompleteAlertModal
        show={!!alertToComplete}
        alert={alertToComplete}
        onHide={() => setAlertToComplete(null)}
        onConfirm={handleConfirmComplete}
        isCompleting={isCompleting}
      />
    </>
  );
};

export default NotificationDropdown;
